import React, { useState, useMemo } from 'react';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.js';
import HelpCircle from 'lucide-react/dist/esm/icons/help-circle.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import { Link } from 'react-router-dom';
import { useContent } from '../../../contexts/ContentContext';
import { useEditMode } from '../../../contexts/EditModeContext';
import { useGameType } from '../../../contexts/GameTypeContext';
import { useTranslations } from '../../../contexts/TranslationContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useSiteConfig } from '../../../contexts/SiteConfigContext';
import { useAuthConfig } from '../../../contexts/AuthConfigContext';
import { resolveCtaTargetCard } from '../../../utils/ctaHelpers';
import { getGameFaqs } from '../../../utils/faqData';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit';

export default function HowToFaqTeaserSection({ section }) {
  const { c } = useContent();
  const { editMode, handleTranslationUpdate } = useEditMode();
  const { gameType, currentGame } = useGameType();
  const { t } = useTranslations();
  const { language } = useLanguage();
  const { siteDetails } = useSiteConfig();
  const { isGuestCheckoutEnabled } = useAuthConfig();
  const [openIndex, setOpenIndex] = useState(null);

  const targetCard = useMemo(() => resolveCtaTargetCard([], siteDetails), [siteDetails]);
  const isGuestCheckout = isGuestCheckoutEnabled();

  const gameFaqDefaults = useMemo(() => {
    return getGameFaqs(gameType, currentGame?.name, t, targetCard, language, isGuestCheckout);
  }, [gameType, currentGame?.name, t, targetCard, language, isGuestCheckout]);

  // Gather top 4 game-specific or general questions
  const teaserQuestions = useMemo(() => {
    const gameSection = gameFaqDefaults.sections.find(s => s.category.startsWith('About')) || gameFaqDefaults.sections[2];
    const questions = gameSection?.questions || [];
    if (questions.length >= 3) return questions;
    const general = gameFaqDefaults.sections[1]?.questions || [];
    return [...questions, ...general].slice(0, 4);
  }, [gameFaqDefaults]);

  return (
    <section className="py-12 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 md:p-8">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400">
            <HelpCircle className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-white">
              <InlineTranslationEdit
                translationKey="faqTeaser.title"
                defaultText={`Frequently Asked Questions — ${currentGame?.name || 'Raffle'}`}
                editMode={editMode}
                onUpdate={handleTranslationUpdate}
              />
            </h2>
            <p className="text-xs text-white/60 mt-0.5">
              {t('faqTeaser.subtitle', 'Quick answers to common questions')}
            </p>
          </div>
        </div>

        <Link
          to="/faq"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors"
        >
          {t('faqTeaser.viewAll', 'View All FAQs')}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="space-y-3">
        {teaserQuestions.map((item, idx) => {
          const isOpen = openIndex === idx;
          return (
            <div
              key={idx}
              className="bg-white/5 border border-white/10 rounded-xl overflow-hidden transition-all hover:bg-white/10"
            >
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : idx)}
                className="w-full px-5 py-4 flex items-center justify-between text-left text-sm font-semibold text-white"
              >
                <span>{item.question}</span>
                <ChevronDown className={`w-4 h-4 text-white/60 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOpen && (
                <div className="px-5 pb-4 pt-1 border-t border-white/10 text-xs text-white/70 leading-relaxed">
                  {item.answer}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
